import { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEvent } from 'effector-react';
import classNames from 'classnames';
import { UserDeletedFile } from '@nimbus/app-analytics-client/lib/events/UserDeletedFile';
import { UserDownloadedFile } from '@nimbus/app-analytics-client/lib/events/UserDownloadedFile';
import { UserRenamedFile } from '@nimbus/app-analytics-client/lib/events/UserRenamedFile';
import { ButtonSize } from '@nx-frontend/shared/ui/button';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useAnalytics } from '@nx-frontend/shared/app-analytics';
import { DocusealDoc } from '@nx-frontend/shared/store/esign/types';
import { deleteFileFx, updateAttachmentFx } from '@nx-frontend/shared/store/feature-files';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  BucketFile,
  FileType,
  PopupType,
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import { ContextMenuItemData, ContextMenuItemType } from '@nx-frontend/shared/types';
import { downloadFile, prepareFileItemUrl } from '@nx-frontend/shared/utils';
import { forcedDownloadFile } from '@nx-frontend/shared/utils/download-file';

export interface OpenFileMenuParams {
  item: BucketFile;
  userId?: number;
  openFileLocationEnabled?: boolean;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  event: MouseEvent;
  fileOpenHost: string;
  canEditOtherUsersFile?: boolean;
  canCreateEsign: boolean;
  isCustomizerMode?: boolean;
  esignDoc?: DocusealDoc | null;
  renameItem?: (itemId: string, newName: string) => void;
  deleteItem?: (itemId: string) => void;
  deleteDisabled?: boolean;
  renameDisabled?: boolean;
}

export const useOpenFileMenu = (): ((params: OpenFileMenuParams) => void) => {
  const [openModal, updateAttachment, deleteFile, closeModal, openContextMenu] = useEvent([
    openModalEvent,
    updateAttachmentFx,
    deleteFileFx,
    closeModalEvent,
    openContextMenuEvent,
  ]);
  const router = useRouter();

  const t = useTranslations('files_dashboard');

  const { analytics } = useAnalytics();

  return ({
    item,
    userId,
    openFileLocationEnabled,
    themeColors,
    themeStyle,
    event,
    fileOpenHost,
    canEditOtherUsersFile,
    canCreateEsign,
    isCustomizerMode,
    esignDoc,
    renameItem,
    deleteItem,
    renameDisabled,
    deleteDisabled,
  }: OpenFileMenuParams) => {
    const itemUrl = prepareFileItemUrl(item.url, fileOpenHost);
    const canEdit = canEditOtherUsersFile || userId === item.userId;
    const items: ContextMenuItemData[] = [];
    items.push({
      type: ContextMenuItemType.DEFAULT,
      title: t('files_dashboard-download'),
      icon: IconsMap.ARROW_REPLY_1,
      action: () => {
        if (item.type === FileType.IMAGE || item.type === FileType.DOC) {
          forcedDownloadFile(itemUrl, item.filename);
        } else {
          downloadFile(itemUrl, item.filename);
        }

        analytics.sendEvent(
          new UserDownloadedFile({
            fileId: item.globalId,
            fileName: item.filename,
            fileUrl: item.url,
            asString: `${item.filename} (${item.globalId})`,
          })
        );
      },
    });
    items.push({
      type: ContextMenuItemType.DEFAULT,
      title: t('files_dashboard-open_new_tab'),
      icon: IconsMap.LOG_OUT_2,
      action: () => {
        window.open(itemUrl, '_blank');
      },
    });
    const destination = item.breadcrumbs && item.breadcrumbs[0] ? item.breadcrumbs[0] : undefined;
    if (destination && destination.url && openFileLocationEnabled) {
      items.push({
        type: ContextMenuItemType.DEFAULT,
        title: t('files_dashboard-file_location'),
        icon: IconsMap.OPEN,
        action: () => {
          if (destination.url) {
            window.open(destination.url, '_blank');
          }
        },
      });
    }
    if (canCreateEsign) {
      let title = t('files_dashboard-add_esign');
      let link = `/esign/${item.bucketId}/file/${item.globalId}`;
      let skip = false;
      if (esignDoc) {
        if (esignDoc.state === 'pending') {
          title = t('files_dashboard-open_esign_draft');
        } else if (esignDoc.state === 'awaiting') {
          const isSigner = esignDoc.submitters.find(
            (submitter) => submitter.userId === userId && submitter.state !== 'complete'
          );
          title = isSigner ? t('files_dashboard-sign_document') : t('files_dashboard-track_esign');
          if (isSigner) {
            link = `/esign/${item.bucketId}/file/${item.globalId}/sign/${isSigner.slug}`;
          }
        } else if (esignDoc.state === 'complete') {
          skip = true;
        }
      }
      if (isCustomizerMode) {
        skip = true;
      }
      if (!skip) {
        items.push({
          type: ContextMenuItemType.DEFAULT,
          title: title,
          icon: IconsMap.OPEN,
          action: () => {
            router.push(link);
          },
        });
      }
    }
    if (canEdit && !renameDisabled) {
      items.push({
        type: ContextMenuItemType.DEFAULT,
        title: t('files_dashboard-rename'),
        icon: IconsMap.EDIT,
        isSeparator: item.target !== 'portalPage',
        action: () => {
          const match = item.filename.match(/^(.+)\.[^.]+$/);
          const filename = match ? match[1] : item.filename;
          openModal({
            title: t('files_dashboard-rename'),
            content: PopupType.RENAME,
            showCloseButton: true,
            closeButtonSize: 18,
            payload: {
              type: 'file',
              value: filename,
              payloadType: PopupType.RENAME,
              themeColors,
              themeStyle: themeStyle,
              onApply: (value: string) => {
                if (value.trim().length) {
                  if (renameItem) {
                    renameItem(item.globalId, value);
                  } else {
                    const updatedFilename = item.filename.replace(filename, value);
                    updateAttachment({ globalId: item.globalId, filename: updatedFilename });

                    analytics.sendEvent(
                      new UserRenamedFile({
                        fileId: item.globalId,
                        oldName: item.filename,
                        newName: updatedFilename,
                        fileUrl: item.url,
                        asString: `${item.filename} ${updatedFilename} (${item.globalId})`,
                      })
                    );
                  }
                }
              },
            },
          });
        },
      });
    }
    if (canEdit && item.target !== 'portalPage' && !deleteDisabled) {
      items.push({
        type: ContextMenuItemType.DEFAULT,
        title: t('files_dashboard-delete'),
        icon: IconsMap.TRASH,
        action: () => {
          openModal({
            title: t('files_dashboard-delete_file'),
            content: PopupType.DIALOG,
            closeButtonSize: 18,
            payload: {
              payloadType: PopupType.DIALOG,
              text: t('files_dashboard-delete_file_description'),
              themeColors,
              themeStyle: themeStyle,
              buttons: [
                {
                  main: false,
                  text: t('files_dashboard-cancel'),
                  size: ButtonSize.MD,
                },
                {
                  main: true,
                  size: ButtonSize.MD,
                  text: t('files_dashboard-delete'),
                  onClick: () => {
                    if (deleteItem) {
                      deleteItem(item.globalId);
                    } else {
                      deleteFile({ globalId: item.globalId });
                    }

                    analytics.sendEvent(
                      new UserDeletedFile({
                        fileId: item.globalId,
                        fileName: item.filename,
                        asString: `${item.filename} (${item.globalId})`,
                      })
                    );

                    closeModal();
                  },
                },
              ],
            },
          });
        },
      });
    }

    openContextMenu({
      items,
      position: calculateContextMenuPosition(event.target, 4),
      themeClassesMenu: classNames(
        getBgClassNames(themeColors, PortalBgType.SURFACE_INNER),
        getTextClassNames(themeColors, PortalTextType.SURFACE),
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
      ),
      themeClassesItem: classNames(
        getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER])
      ),
    });
  };
};
